import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChartMovement, ChartRank, LoadingState, MonoLabel, Row, SectionHeader, Star } from '@/component';
import { useBillboard, useFavourites, type BillboardScope } from '@/hook';
import { formatQueueCount } from '@/lib/chart';

const ROWS = 8;
const SCOPES = [
  { value: 'global', label: 'Global' },
  { value: 'friends', label: 'Friends' },
] as const satisfies ReadonlyArray<{ value: BillboardScope; label: string }>;

/** Home's Top queued: this week's chart in eight rows, scoped global or to
 * friends, with the full table a link away. */
export function HomeTopQueued() {
  const [scope, setScope] = useState<BillboardScope>('global');
  const chart = useBillboard(scope, ROWS);
  const { favourites, toggle } = useFavourites();

  return (
    <section>
      <SectionHeader
        title="Top queued"
        action={
          <>
            {SCOPES.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={option.value === scope}
                onClick={() => setScope(option.value)}
              >
                <MonoLabel tone={option.value === scope ? 'ink' : 'faint'}>{option.label}</MonoLabel>
              </button>
            ))}
            <Link to="/billboard">
              <MonoLabel tone="mute">Billboard →</MonoLabel>
            </Link>
          </>
        }
      />
      {chart === undefined && <LoadingState label="Loading the chart…" />}
      {chart && chart.rows.length === 0 && (
        <p className="py-6 text-sm text-mute">
          Nothing queued yet this week. The chart fills as songs go on Aeon systems.
        </p>
      )}
      <div key={scope}>
        {chart?.rows.map((row, index) => (
          <Row
            key={row.trackId}
            className="chart-row-in gap-[18px]"
            style={{ animationDelay: `${index * 25}ms` }}
          >
            <ChartRank rank={row.rank} className="w-6 shrink-0 text-[17px]" />
            <Link to="/tracks/$trackId" params={{ trackId: row.trackId }} className="min-w-0 flex-1">
              <div className="truncate text-[15px] font-semibold tracking-[-0.01em]">{row.title}</div>
              <div className="mt-px truncate text-[13px] text-mute">{row.artist}</div>
            </Link>
            <span className="w-[52px] shrink-0 text-right font-mono text-xs tabular-nums text-mute">
              {formatQueueCount(row.count)}
            </span>
            <ChartMovement movement={row.movement} className="w-[34px] shrink-0 text-right" />
            <Star on={favourites.has(row.trackId)} onToggle={() => toggle(row.trackId)} />
          </Row>
        ))}
      </div>
    </section>
  );
}
